
import { faLayerGroup, faList } from '@fortawesome/pro-duotone-svg-icons';
import { faLocationCrosshairs, faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, CircularProgress, Divider, IconButton, Link, Table, TableBody, TableCell, TableRow, Typography, styled, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { ControlPosition, InfoWindow, Map, MapControl, Marker as MapMarker, MapMouseEvent, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SiteEventMarker, SiteEventType, Species } from '../api/dtos/dtos';
import RoadMapLayer from '../assets/images/map/layers/roadmap.png';
import SatelliteLayer from '../assets/images/map/layers/satellite.png';
import ManagementMapStyle from '../assets/map/styles/managementmap.json';
import ChooseLocationBanner from '../components/banners/ChooseLocationBanner';
import Center from '../components/common/Center';
import Dropdown from '../components/common/Dropdown';
import Image from '../components/common/Image';
import FormButton from '../components/map/FormButton';
import AuthContext from '../contexts/auth/AuthContext';
import MapContext, { MapEvent } from '../contexts/data/MapContext';
import MapFormContext from '../contexts/data/MapFormContext';
import { SpeciesContext } from '../contexts/data/SpeciesContext';
import SpeciesFormsContext from '../contexts/data/SpeciesFormsContext';
import useGetMapLegend from '../hooks/data/useGetMapLegend';
import useGetPolygonByRegionId from '../hooks/data/useGetPolygonByRegionId';
import usePostSiteEventMarkerCollection from '../hooks/data/usePostSiteEventMarkerCollection';
import useMapEvent from '../hooks/map/useMapEvent';
import { AppSettings } from '../AppSettings';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

interface IKmlLayerProps {
    show: boolean;
    regionId: number | undefined;
}

interface IPolygonLayerProps {
    show: boolean;
    regionId: number | undefined;
}

const StyledIconButton = styled(IconButton)(() => ({
    width: '50px',
    height: '50px',
    backgroundColor: 'white',
    boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',

    "&:hover": {
        backgroundColor: 'white',
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:hover svg": {
        color: 'black'
    }
}));

const ZoomWrapper = styled(Box)(() => ({
    width: '40px',
    height: '80px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '5px',
    backgroundColor: 'white',
    justifyContent: 'space-around',
    overflow: 'hidden',
    color: 'grey'
}));

const StyledCenterZoom = styled(Center)(({ theme }) => ({
    height: '100%',

    "&:hover": {
        color: theme.palette.primary.main,
        cursor: 'pointer'
    }
}));

const StyledLegendTableCell = styled(TableCell)(() => ({
    padding: '5px 5px 5px 15px',
    border: 'none',

    "&:nth-of-type(2)": {
        paddingLeft: '0'
    }
}));

const StyledLayerButton = styled(Box)(() => ({
    padding: '10px',
    borderRadius: '5px',

    "&:hover": {
        cursor: 'pointer',
        backgroundColor: grey[200]
    }
}));

const KmlLayer: FC<IKmlLayerProps> = ({
    show,
    regionId
}) => {
    //
    // -- Hooks --
    const map = useMap();
    const kmlApi = useMapsLibrary('maps');

    //
    // -- States --
    //
    // Kml state
    const [kmlLayers, setKmlLayers] = useState<google.maps.KmlLayer[]>([]);

    // Load KML file path.
    const { data: kmlFileUrl, loading: loadingkmlFileUrl } = useGetPolygonByRegionId(regionId ? { regionId: regionId } : undefined, { errorRetryCount: 0 });

    //
    // -- useEffects --
    //
    // Create/Clear Kml Layers
    useEffect(() => {
        if (!kmlApi || loadingkmlFileUrl || !kmlFileUrl || !show) {
            return;
        }

        // Clear current KML layers
        kmlLayers.forEach(layer => layer.setMap(null));
        setKmlLayers([]);
        const kmlLayer = new kmlApi.KmlLayer({
            url: kmlFileUrl,
            clickable: true,
            preserveViewport: false,
            map: map
        });

        // Set group KML layers
        const newKmlLayers: google.maps.KmlLayer[] = [];
        kmlLayer.setMap(map);
        newKmlLayers.push(kmlLayer);

        setKmlLayers(newKmlLayers);


    }, [map, kmlApi, kmlFileUrl, loadingkmlFileUrl, show]);

    //
    // -- Render --
    return (
        null
    );
}

const PolygonLayer: FC<IPolygonLayerProps> = ({
    show,
    regionId
}) => {
    //
    // -- Hooks --
    const theme = useTheme();
    const map = useMap();
    const polygonApi = useMapsLibrary('maps');

    //
    // -- States --
    //
    // Polygon state
    const [polygonLayers, setPolygonLayers] = useState<google.maps.Polygon[]>([]);

    // Load Polygon.
    const { data: polygonString, loading: loadingpolygonString } = useGetPolygonByRegionId(regionId ? { regionId: regionId } : undefined, { errorRetryCount: 0 });

    //
    // -- useEffects --
    //
    // Create/Clear Polygon Layers
    useEffect(() => {
        if (!polygonApi || loadingpolygonString || !show) return;

        if (!polygonString) {
            // Clear current polygon layers
            polygonLayers.forEach(layer => layer.setMap(null));
            setPolygonLayers([]);
            return;
        }

        // Convert polygon string to json
        let jsonPolygon = JSON.parse(polygonString);
        if (jsonPolygon === null || jsonPolygon === undefined) return;

        // Clear current polygon layers
        polygonLayers.forEach(layer => layer.setMap(null));
        setPolygonLayers([]);

        // Create polygon from json
        const polygonLayer = new polygonApi.Polygon({
            paths: [...jsonPolygon],
            fillColor: theme.palette.primary.main,
            fillOpacity: 0.3,
            strokeColor: theme.palette.primary.main,
            strokeOpacity: 0.75
        });

        // Set polygon layers
        const newPolygonLayers: google.maps.Polygon[] = [];
        polygonLayer.setMap(map);
        newPolygonLayers.push(polygonLayer);

        setPolygonLayers(newPolygonLayers);

    }, [map, polygonApi, polygonString, loadingpolygonString, show]);

    //
    // -- Render --
    return (
        null
    );
}

const ManagementMap: FC = () => {
    //
    // -- Hooks --
    const theme = useTheme();
    const map = useMap();

    // 
    // -- Params --
    const { species: speciesName } = useParams();

    //
    // -- Contexts --
    const { user } = useContext(AuthContext);
    const { species: { species } } = useContext(SpeciesContext);
    const { filter, mapCenter, setMapCenter } = useContext(MapContext);
    const {
        drawerOpen: formDrawerOpen,
        openDrawer,
        maximiseDrawer,
        setMapFormData,
        mapFormData,
        setSelectingLocationCoords,
        selectingLocationCoords,
        setRecordToEdit,
        setSiteEventType
    } = useContext(MapFormContext);
    const { selectedForm, canClickMap, selectingLocation } = useContext(SpeciesFormsContext);

    // 
    // -- States --
    const [position, setPosition] = useState({ latitude: -29, longitude: 135 });
    const [selectedRegionId, setSelectedRegionId] = useState<number | undefined>(undefined);

    // Center the initial map camera onto "center" of AUS
    const defaultProps = {
        center: {
            lat: mapCenter?.latitude ?? -29,
            lng: mapCenter?.longitude ?? 135
        },
        zoom: mapCenter?.zoom ?? 4
    };

    const isAdmin = useMemo(() => {
        if (user?.roles?.includes("admin")) {
            return true;
        }
        if (user?.roles?.includes("superadmin")) {
            return true;
        }
        return false;
    }, [user]);

    useEffect(() => {
        return () => {
            setMapCenter(undefined);
        }
    }, [setMapCenter]);

    // Load the map legend content.
    // If species is set to "None" or undefined, don't send the request.
    const { data: legend, loading: legendLoading } = useGetMapLegend(species && species !== Species.None ? { species: species } : undefined, { errorRetryCount: 0 });

    useEffect(() => {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(function (position) {
                setPosition({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            });
        }
    }, []);

    // Get the map markers.
    // If species is set to "None" or undefined, don't send the request.
    const markersRequest = useMemo(() => {
        if (!species || species === Species.None) {
            return undefined;
        }

        return {
            species: species,
            siteEventTypes: [SiteEventType.Management]
        };
    }, [user, filter, species]);
    const { data: siteEventMarkers, loading: _siteEventMarkersLoading, refresh: siteEventMarkerRefresh } = usePostSiteEventMarkerCollection(markersRequest, { revalidateOnFocus: false, errorRetryCount: 0 });

    useMapEvent(MapEvent.Refresh, async () => {
        await siteEventMarkerRefresh();
    })

    const [mapTypeId, setMapTypeId] = useState<"roadmap" | "satellite">("roadmap");
    const [selectedMarker, setSelectedMarker] = useState<{ siteEventMarker: SiteEventMarker, mapMarker: google.maps.Marker }>();
    const [buttonStates, setButtonStates] = useState<{ legend: boolean, layer: boolean }>({
        legend: false,
        layer: false
    });

    const verifiedMarkers = useMemo(() => {
        const res = [];

        // Selecting Form Location Marker
        if (formDrawerOpen) {
            const rawMarker: { current: google.maps.Marker | null } = {
                current: null
            }

            if (selectingLocation) {
                if (selectingLocationCoords) {
                    res.push(
                        <MapMarker
                            key={`selectingLocationMarker`}
                            ref={ref => rawMarker.current = ref}
                            position={{
                                lat: selectingLocationCoords.lat,
                                lng: selectingLocationCoords.lng,
                            }}
                            icon={{
                                url: `${AppSettings.baseUrl}/images/map/newMarker.${speciesName?.replace("scan", "").replace("alert", "")}.png`,
                                anchor: new google.maps.Point(10, 80)
                            }}
                            clickable={false}
                            draggable={true}
                            onDragEnd={(event: google.maps.MapMouseEvent) => {
                                setSelectingLocationCoords({
                                    lat: Number(event.latLng?.lat().toString()),
                                    lng: Number(event.latLng?.lng().toString())
                                });
                            }}
                        />
                    );
                }
                else {
                    res.push(
                        <MapMarker
                            key={`selectingLocationMarker`}
                            ref={ref => rawMarker.current = ref}
                            position={{
                                lat: position.latitude,
                                lng: position.longitude,
                            }}
                            icon={{
                                url: `${AppSettings.baseUrl}/images/map/newMarker.${speciesName?.replace("scan", "").replace("alert", "")}.png`,
                                anchor: new google.maps.Point(10, 80)
                            }}
                            clickable={false}
                            draggable={true}
                            onDragEnd={(event: google.maps.MapMouseEvent) => {
                                setSelectingLocationCoords({
                                    lat: Number(event.latLng?.lat().toString()),
                                    lng: Number(event.latLng?.lng().toString())
                                });
                            }}
                        />
                    );
                }
                return res;
            }
            else if (selectedForm) {
                if (mapFormData?.[selectedForm.name]?.["Latitude"] && mapFormData?.[selectedForm.name]?.["Longitude"]) {
                    res.push(
                        <MapMarker
                            key={`selectingLocationMarker`}
                            ref={ref => rawMarker.current = ref}
                            position={{
                                lat: +mapFormData[selectedForm.name]["Latitude"],
                                lng: +mapFormData[selectedForm.name]["Longitude"],
                            }}
                            icon={{
                                url: `${AppSettings.baseUrl}/images/map/newMarker.${speciesName?.replace("scan", "").replace("alert", "")}.png`,
                                anchor: new google.maps.Point(10, 80)
                            }}
                            clickable={false}
                            draggable={true}
                            onDragEnd={(event: google.maps.MapMouseEvent) => {
                                setMapFormData({
                                    ...mapFormData,
                                    [selectedForm!.name]: {
                                        ...mapFormData?.[selectedForm!.name],
                                        "Latitude": event.latLng?.lat().toString(),
                                        "Longitude": event.latLng?.lng().toString()
                                    }
                                });
                            }}
                        />
                    );
                }
                else {
                    res.push(
                        <MapMarker
                            key={`selectingLocationMarker`}
                            ref={ref => rawMarker.current = ref}
                            position={{
                                lat: position.latitude,
                                lng: position.longitude
                            }}
                            icon={{
                                url: `${AppSettings.baseUrl}/images/map/newMarker.${speciesName?.replace("scan", "").replace("alert", "")}.png`,
                                anchor: new google.maps.Point(10, 80)
                            }}
                            clickable={false}
                            draggable={true}
                            onDragEnd={(event: google.maps.MapMouseEvent) => {
                                setMapFormData({
                                    ...mapFormData,
                                    [selectedForm!.name]: {
                                        ...mapFormData?.[selectedForm!.name],
                                        "Latitude": event.latLng?.lat().toString(),
                                        "Longitude": event.latLng?.lng().toString()
                                    }
                                });
                            }}
                        />
                    );
                }
            }
            return res;
        }

        if (!siteEventMarkers || siteEventMarkers.markers.length === 0) {
            return undefined;
        }

        // Site Event Markers
        for (let se of siteEventMarkers.markers) {
            const lat = se.latitude;
            const lng = se.longitude;
            if (!lat || !lng) {
                continue;
            }

            const rawMarker: { current: google.maps.Marker | null } = {
                current: null
            }
            // Filter the marker results to only display those which match the filter
            const speciesMatch = se.species === species;
            const sightingTypeMatch = se.siteEventType === SiteEventType.Management;

            if (speciesMatch &&
                sightingTypeMatch
            ) {
                res.push(
                    <MapMarker
                        key={`${se.speciesSiteEventType}-${se.id}`}
                        ref={ref => rawMarker.current = ref}
                        position={{ lat, lng }}
                        icon={{
                            url: `${AppSettings.baseUrl}/images/map/markers/${se.icon}`
                        }}
                        clickable={!formDrawerOpen}
                        onClick={(e) => {
                            e.domEvent.preventDefault();
                            // IACWEB-1336 Comment this out for now. Fix it if we need the management map.
                            /*if ((se.siteEvent as SiteEventManagementWildDog).showKml && (se.siteEvent as SiteEventManagementWildDog).regionId) {
                                setSelectedRegionId((se.siteEvent as SiteEventManagementWildDog).regionId);
                            }

                            if (!rawMarker.current) {
                                setSelectedMarker(undefined);
                                return;
                            }
                            setSelectedMarker({ siteEventMarker: se, mapMarker: rawMarker.current });*/
                        }}
                    />
                );
            }
        }

        return res;
    }, [
        formDrawerOpen,
        siteEventMarkers,
        setSelectedMarker,
        mapFormData,
        selectedForm,
        selectingLocation,
        selectingLocationCoords,
        setSelectedRegionId
    ]);


    const handleGeoLocationClick = useCallback(() => {
        // If the navigator and map both have been intialised
        if (navigator.geolocation && map) {
            navigator.geolocation.getCurrentPosition(
                (position: GeolocationPosition) => {
                    const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };

                    // TODO: Set marker dictating the user's geo location

                    map.setCenter(pos);
                    map.setZoom(16);
                },
                () => {
                    // TODO: Handle geo nav error
                }
            );

            // Else, notify user that this is not working
        } else {
            // TODO: Handle navigator error
        }
    }, [map]);

    const handleMapClick = useCallback((event: MapMouseEvent) => {
        if (event.type === 'click') {
            if (canClickMap) {
                // Save lat and lng to provider
                setMapFormData({
                    ...mapFormData,
                    [selectedForm!.name]: {
                        ...mapFormData?.[selectedForm!.name],
                        "Latitude": event.detail.latLng?.lat.toString(),
                        "Longitude": event.detail.latLng?.lng.toString()
                    }
                });
            }
            if (selectingLocation) {
                // Temp store lat and lng to provider
                setSelectingLocationCoords({
                    lat: Number(event.detail.latLng?.lat.toString()),
                    lng: Number(event.detail.latLng?.lng.toString())
                });
            }
        }
    }, [selectedForm, setMapFormData, mapFormData, canClickMap, selectingLocation, setSelectingLocationCoords]);

    const zoomIn = useCallback(() => {
        if (map) {
            map.setZoom((map.getZoom() ?? defaultProps.zoom) + 1);
        }
    }, [map]);

    const zoomOut = useCallback(() => {
        if (map) {
            map.setZoom((map.getZoom() ?? defaultProps.zoom) - 1);
        }
    }, [map]);


    const getImageLink = (galleryId: number | undefined, photoId: number | undefined, thumbnail: boolean) => {
        if (thumbnail) {
            return `${AppSettings.baseUrl}/gallery/${galleryId}/photo/${photoId}/thumbnail`;
        }
        return `${AppSettings.baseUrl}/gallery/${galleryId}/photo/${photoId}`;
    };

    const handleEditRecordClick = (siteEventType: SiteEventType, recordId: number) => {
        if (!siteEventType || !recordId) return;
        var hasImage = false;
        setRecordToEdit({ siteEventType, recordId, hasImage });
        setSiteEventType(siteEventType)
        openDrawer();
        maximiseDrawer();
    }

    return (

        <Map
            //mapId={"feral-scan-management-map"}
            mapTypeId={mapTypeId}
            styles={ManagementMapStyle}
            /* -- Default UI Controls -- */
            zoomControl={false}
            mapTypeControl={false}
            fullscreenControl={false}
            streetViewControl={false}
            /* -- Default Map View -- */
            defaultCenter={defaultProps.center}
            defaultZoom={defaultProps.zoom}
            /* -- Map Events -- */
            onClick={(ev: MapMouseEvent) => handleMapClick(ev)}
        >
            {/* -- Markers -- */}
            {
                verifiedMarkers
            }

            {/* -- KMLs -- */}
            <KmlLayer show regionId={selectedRegionId} />

            {/* -- Ploygons -- */}
            <PolygonLayer show regionId={selectedRegionId} />

            {/* -- Marker Popup -- */}
            {
                !!selectedMarker && (
                    <InfoWindow
                        style={{ maxWidth: '250px', maxHeight: '400px', overflowY: 'scroll', scrollbarWidth: 'none' }}
                        anchor={selectedMarker.mapMarker}
                        onClose={() => { setSelectedMarker(undefined); setSelectedRegionId(undefined) }}
                    >
                        { parse(DOMPurify.sanitize(selectedMarker.siteEventMarker.marker.content ?? "")) }
                        {selectedMarker.siteEventMarker.marker.canEditRecord && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: '10px', paddingBottom: '10px' }}>
                                <Link
                                    sx={{ cursor: 'pointer', my: '2px' }}
                                    onClick={() => {
                                        if (selectedMarker.siteEventMarker.marker.siteEventType && selectedMarker.siteEventMarker.siteEvent.id)
                                        handleEditRecordClick(
                                            selectedMarker.siteEventMarker.marker.siteEventType,
                                            selectedMarker.siteEventMarker.siteEvent.id
                                        )
                                    }}
                                >
                                    Edit Record
                                </Link>
                                {(selectedMarker.siteEventMarker.marker.galleryId !== 0 &&
                                    selectedMarker.siteEventMarker.marker.galleryPhotoId !== 0) && (
                                        <>
                                            <Box height="20px" />
                                            <Link
                                                href={getImageLink(selectedMarker.siteEventMarker.marker.galleryId, selectedMarker.siteEventMarker.marker.galleryPhotoId, true)}
                                                target="_blank"
                                                sx={{ cursor: 'pointer', my: '2px' }}
                                            >
                                                Click to enlarge
                                            </Link>
                                            <Box
                                                component="img"
                                                sx={{ width: 'auto', height: 'auto' }}
                                                alt="Failed to load image."
                                                src={getImageLink(selectedMarker.siteEventMarker.marker.galleryId, selectedMarker.siteEventMarker.marker.galleryPhotoId, true)}
                                            />
                                        </>
                                    )}
                            </Box>
                        )}
                    </InfoWindow>
                )
            }

            {/* -- Map Forms -- */}
            {!selectingLocation && isAdmin && (
                <MapControl position={ControlPosition.LEFT_TOP}>
                    <Box
                        sx={{
                            margin: '16px 0px 16px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                            width: '0px',
                        }}
                    >
                        <FormButton />
                    </Box>
                </MapControl>
            )}

            {/* -- Map Legend and Layer Views -- */}
            <MapControl position={ControlPosition.RIGHT_TOP}>
                <Box
                    sx={{
                        margin: '16px 16px 16px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'end',
                        width: '0px',
                    }}
                >
                    {/* -- Map Layer -- */}
                    <StyledIconButton
                        sx={{
                            width: '40px',
                            height: '40px',
                            marginBottom: '16px',
                            color: buttonStates.layer ? theme.palette.primary.main : 'normal',
                            borderRadius: buttonStates.layer ? '0 50% 50% 0' : '50%',

                            "&:hover svg": {
                                color: buttonStates.layer ? theme.palette.primary.main : 'black'
                            }
                        }}
                        onClick={() => setButtonStates(c => ({ legend: false, layer: !c.layer }))}
                    >
                        <FontAwesomeIcon fontSize='1.3rem' icon={faLayerGroup} />
                        <Dropdown
                            anchor={null}
                            open={buttonStates.layer}
                            position='left'
                            sx={{ borderRadius: '5px 0 5px 5px' }}
                        >
                            <Box>
                                <StyledLayerButton
                                    onClick={() => {
                                        setMapTypeId("roadmap");
                                        setButtonStates({
                                            legend: false,
                                            layer: false,
                                        });
                                    }}
                                >
                                    <Image width='100px' height='100px' src={RoadMapLayer} />
                                    <Typography color="#333">Default</Typography>
                                </StyledLayerButton>
                                <Divider />
                                <StyledLayerButton
                                    onClick={() => {
                                        setMapTypeId("satellite");
                                        setButtonStates({
                                            legend: false,
                                            layer: false,
                                        });
                                    }}
                                >
                                    <Image width='100px' height='100px' src={SatelliteLayer} />
                                    <Typography color="#333">Satellite</Typography>
                                </StyledLayerButton>
                            </Box>
                        </Dropdown>
                    </StyledIconButton>

                    {/* -- Map Legend -- */}
                    {!selectingLocation && (
                        <StyledIconButton
                            sx={{
                                width: '40px',
                                height: '40px',
                                marginBottom: '16px',
                                color: buttonStates.legend ? theme.palette.primary.main : 'normal',
                                borderRadius: buttonStates.legend ? '0 50% 50% 0' : '50%',

                                "&:hover svg": {
                                    color: buttonStates.legend ? theme.palette.primary.main : 'black'
                                }
                            }}
                            onClick={() => setButtonStates(c => ({ layer: false, legend: !c.legend }))}
                        >
                            <Center>
                                <FontAwesomeIcon fontSize='1.3rem' icon={faList} />
                                {
                                    legendLoading ?
                                        <CircularProgress
                                            sx={{
                                                color: theme.palette.primary.main,
                                                position: 'absolute',
                                                maxHeight: '40px',
                                                maxWidth: '40px'
                                            }}
                                        /> :
                                        null
                                }
                            </Center>
                            <Dropdown
                                anchor={null}
                                open={buttonStates.legend}
                                position='left'
                                sx={{ borderRadius: '5px 0 5px 5px' }}
                            >
                                {
                                    legend &&
                                    <Box>
                                        <Table sx={{ width: '250px', marginY: '10px', marginRight: '15px' }}>
                                            <TableBody>
                                                {
                                                    legend.filter(x => x.title?.includes("Management")).map((x, idx) => (
                                                        <TableRow
                                                            key={`map-legend-${idx}`}
                                                        >
                                                            <StyledLegendTableCell width='16px'>
                                                                <Center>
                                                                    <Image src={x.imageUrl} />
                                                                </Center>
                                                            </StyledLegendTableCell>
                                                            <StyledLegendTableCell>
                                                                <Typography
                                                                    sx={{ color: '#5F2B10' }}
                                                                    fontWeight='bold'
                                                                    fontSize='12px'
                                                                >
                                                                    {x.title?.replace("Management:", "")}
                                                                </Typography>
                                                            </StyledLegendTableCell>
                                                        </TableRow>
                                                    ))
                                                }
                                            </TableBody>
                                        </Table>
                                    </Box>
                                }
                            </Dropdown>
                        </StyledIconButton>
                    )}

                    {/* -- Map Zoom -- */}
                    <ZoomWrapper>
                        <StyledCenterZoom
                            onClick={() => zoomIn()}
                        >
                            <FontAwesomeIcon fontSize="1.2rem" icon={faPlus} />
                        </StyledCenterZoom>
                        <Divider />
                        <StyledCenterZoom
                            onClick={() => zoomOut()}
                        >
                            <FontAwesomeIcon fontSize="1.2rem" icon={faMinus} />
                        </StyledCenterZoom>
                    </ZoomWrapper>
                </Box>
            </MapControl>

            {/* -- User's Geo Location -- */}
            <MapControl position={ControlPosition.RIGHT_BOTTOM}>
                <Box margin='0 16px 16px 0'>
                    <StyledIconButton
                        onClick={handleGeoLocationClick}
                    >
                        <FontAwesomeIcon icon={faLocationCrosshairs} />
                    </StyledIconButton>
                </Box>
            </MapControl>

            {/* -- Choose location banner -- */}
            {selectingLocation && (
                <ChooseLocationBanner />
            )}

        </Map>
    );
}


const ManagementMapPage: FC = () => {
    return (
        <div style={{ flex: '1 1 auto', alignSelf: 'stretch' }}>
            <ManagementMap />
        </div>
    )
}

export default ManagementMapPage;
